import React, { createContext, useContext, useState, useEffect } from 'react';

// Create the context
const UserPreferencesContext = createContext();

// Currency conversion rates (approximate - in production, fetch from API)
const currencyRates = {
  USD: 1,
  EUR: 0.92,
  GBP: 0.79,
  JPY: 149.50,
  CNY: 7.24,
  CAD: 1.36,
  AUD: 1.53,
  CHF: 0.88,
  HKD: 7.82,
  SGD: 1.34,
  INR: 83.12,
  KRW: 1298.50,
  BRL: 4.97,
  MXN: 17.15,
  BTC: 0.0000099,
  ETH: 0.00026,
};

// Currency symbols
const currencySymbols = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  CNY: '¥',
  CAD: 'C$',
  AUD: 'A$',
  CHF: 'Fr',
  HKD: 'HK$',
  SGD: 'S$',
  INR: '₹',
  KRW: '₩',
  BRL: 'R$',
  MXN: '$',
  BTC: '₿',
  ETH: 'Ξ',
};

// Region to exchanges mapping
const regionExchanges = {
  GLOBAL: ['NYSE', 'NASDAQ', 'TSX', 'LSE', 'XETRA', 'EURONEXT', 'JPX', 'SSE', 'SZSE', 'HKEX', 'SGX', 'ASX', 'BSE', 'NSE', 'B3', 'KRX'],
  US: ['NYSE', 'NASDAQ', 'AMEX', 'CBOE'],
  CA: ['TSX', 'TSXV', 'CSE'],
  UK: ['LSE', 'AIM'],
  EU: ['XETRA', 'EURONEXT', 'SIX', 'BME'],
  DE: ['XETRA', 'FRA'],
  FR: ['EURONEXT'],
  JP: ['JPX', 'TSE'],
  CN: ['SSE', 'SZSE'],
  HK: ['HKEX'],
  SG: ['SGX'],
  AU: ['ASX'],
  IN: ['BSE', 'NSE'],
  BR: ['B3'],
  KR: ['KRX'],
};

// Translations (comprehensive)
const translations = {
  English: {
    // Navigation
    dashboard: 'Dashboard',
    markets: 'Markets',
    crypto: 'Crypto',
    news: 'News',
    analysis: 'AI Analysis',
    portfolio: 'Portfolio',
    settings: 'Settings',
    
    // Common actions
    search: 'Search stocks...',
    searchNews: 'Search news...',
    viewAll: 'View All',
    seeAll: 'See All',
    analyze: 'Analyze',
    refresh: 'Refresh',
    save: 'Save',
    cancel: 'Cancel',
    edit: 'Edit',
    delete: 'Delete',
    confirm: 'Confirm',
    back: 'Back',
    next: 'Next',
    loading: 'Loading...',
    
    // Dashboard
    marketMovers: 'Market Movers',
    cryptoMovers: 'Crypto Movers',
    watchlist: 'Watchlist',
    quickActions: 'Quick Actions',
    aiInsights: 'AI Insights',
    goodMorning: 'Good morning',
    goodAfternoon: 'Good afternoon',
    goodEvening: 'Good evening',
    portfolioValue: 'Portfolio Value',
    totalGain: 'Total Gain',
    todayGain: "Today's Gain",
    allAccounts: 'All accounts',
    fearGreed: 'Fear & Greed Index',
    economicIndicators: 'Economic Indicators',
    
    // Market data
    price: 'Price',
    change: 'Change',
    volume: 'Volume',
    marketCap: 'Market Cap',
    high: 'High',
    low: 'Low',
    open: 'Open',
    close: 'Close',
    
    // Sentiment
    bullish: 'Bullish',
    bearish: 'Bearish',
    neutral: 'Neutral',
    
    // News
    breaking: 'BREAKING',
    allNews: 'All News',
    latestNews: 'Latest News',
    trending: 'Trending',
    economy: 'Economy',
    earnings: 'Earnings',
    policy: 'Policy',
    readMore: 'Read More',
    source: 'Source',
    relatedTickers: 'Related Tickers',
    
    // Portfolio
    holdings: 'Holdings',
    totalValue: 'Total Value',
    dayChange: 'Day Change',
    totalReturn: 'Total Return',
    shares: 'Shares',
    avgCost: 'Avg Cost',
    currentPrice: 'Current Price',
    gainLoss: 'Gain/Loss',
    
    // Markets page
    allMarkets: 'All Markets',
    stocks: 'Stocks',
    indices: 'Indices',
    commodities: 'Commodities',
    forex: 'Forex',
    
    // Settings
    profile: 'Profile',
    account: 'Account',
    preferences: 'Preferences',
    notifications: 'Notifications',
    security: 'Security',
    appearance: 'Appearance',
    language: 'Language',
    region: 'Region',
    currency: 'Currency',
    darkMode: 'Dark Mode',
    lightMode: 'Light Mode',
    
    // Time
    today: 'Today',
    yesterday: 'Yesterday',
    thisWeek: 'This Week',
    thisMonth: 'This Month',
    thisYear: 'This Year',
    
    // API Status
    connected: 'Connected',
    disconnected: 'Disconnected',
    apiStatus: 'API Status',
    live: 'LIVE',
    
    // Analysis Page
    marketOverview: 'Market Overview',
    recentAnalyses: 'Recent Analyses',
    confidence: 'Confidence',
    analysisTools: 'Analysis Tools',
    technicalAnalysis: 'Technical Analysis',
    fundamentalAnalysis: 'Fundamental Analysis',
    priceTargets: 'Price Targets',
    riskAssessment: 'Risk Assessment',
    sentimentAnalysis: 'Sentiment Analysis',
    valuationModels: 'Valuation Models',
    low: 'Low',
    aboveAvg: 'Above Avg',
    breadth: 'Breadth',
    
    // Portfolio Page
    trackPortfolio: 'Track your investments and performance',
    totalValue: 'Total Value',
    totalGain: 'Total Gain',
    return: 'Return',
    totalHoldings: 'Total Holdings',
    assetAllocation: 'Asset Allocation',
    performance: 'Performance',
    stocksBySector: 'Stocks by Sector',
    dividends: 'Dividends',
    
    // Portfolio additional
    stocks: 'Stocks',
    holdings: 'Holdings',
    sector: 'Sector',
    shares: 'Shares',
    avgCost: 'Avg Cost',
    value: 'Value',
    gainLoss: 'Gain/Loss',
    asset: 'Asset',
    amount: 'Amount',
    allocation: 'Allocation',
    assets: 'Assets',
    other: 'Other',
    estAnnual: 'Est. Annual',
    avgYield: 'Avg Yield',
    dividendStocks: 'Dividend Stocks',
    dividendPayingHoldings: 'Dividend Paying Holdings',
    dividendPerShare: 'Dividend/Share',
    yield: 'Yield',
    annualIncome: 'Annual Income',
    quarterly: 'Quarterly',
    recentDividendPayments: 'Recent Dividend Payments',
    currentPrice: 'Current Price',
    
    // Settings Page
    generalSettings: 'General Settings',
    themeLanguageDisplay: 'Theme, language, and display preferences',
    switchTheme: 'Switch between dark and light theme',
    timezone: 'Timezone',
    manageNotifications: 'Manage how you receive alerts and updates',
    emailNotifications: 'Email Notifications',
    receiveEmail: 'Receive updates via email',
    pushNotifications: 'Push Notifications',
    browserMobile: 'Browser and mobile alerts',
    priceAlerts: 'Price Alerts',
    notifiedPrice: 'Get notified on price movements',
    manageAccount: 'Manage your account and preferences',
    viewProfile: 'View Profile',
    logOut: 'Log Out',
  },
  Spanish: {
    // Navigation
    dashboard: 'Panel',
    markets: 'Mercados',
    crypto: 'Cripto',
    news: 'Noticias',
    analysis: 'Análisis IA',
    portfolio: 'Cartera',
    settings: 'Ajustes',
    
    // Common actions
    search: 'Buscar acciones...',
    searchNews: 'Buscar noticias...',
    viewAll: 'Ver Todo',
    seeAll: 'Ver Todo',
    analyze: 'Analizar',
    refresh: 'Actualizar',
    save: 'Guardar',
    cancel: 'Cancelar',
    edit: 'Editar',
    delete: 'Eliminar',
    confirm: 'Confirmar',
    back: 'Atrás',
    next: 'Siguiente',
    loading: 'Cargando...',
    
    // Dashboard
    marketMovers: 'Movimientos del Mercado',
    cryptoMovers: 'Movimientos Cripto',
    watchlist: 'Lista de Seguimiento',
    quickActions: 'Acciones Rápidas',
    aiInsights: 'Perspectivas IA',
    goodMorning: 'Buenos días',
    goodAfternoon: 'Buenas tardes',
    goodEvening: 'Buenas noches',
    portfolioValue: 'Valor de Cartera',
    totalGain: 'Ganancia Total',
    todayGain: 'Ganancia de Hoy',
    allAccounts: 'Todas las cuentas',
    fearGreed: 'Índice de Miedo y Codicia',
    economicIndicators: 'Indicadores Económicos',
    
    // Market data
    price: 'Precio',
    change: 'Cambio',
    volume: 'Volumen',
    marketCap: 'Cap. de Mercado',
    high: 'Máximo',
    low: 'Mínimo',
    open: 'Apertura',
    close: 'Cierre',
    
    // Sentiment
    bullish: 'Alcista',
    bearish: 'Bajista',
    neutral: 'Neutral',
    
    // News
    breaking: 'URGENTE',
    allNews: 'Todas las Noticias',
    latestNews: 'Últimas Noticias',
    trending: 'Tendencias',
    economy: 'Economía',
    earnings: 'Ganancias',
    policy: 'Política',
    readMore: 'Leer Más',
    source: 'Fuente',
    relatedTickers: 'Símbolos Relacionados',
    
    // Portfolio
    holdings: 'Posiciones',
    totalValue: 'Valor Total',
    dayChange: 'Cambio del Día',
    totalReturn: 'Retorno Total',
    shares: 'Acciones',
    avgCost: 'Costo Promedio',
    currentPrice: 'Precio Actual',
    gainLoss: 'Ganancia/Pérdida',
    
    // Markets page
    allMarkets: 'Todos los Mercados',
    stocks: 'Acciones',
    indices: 'Índices',
    commodities: 'Materias Primas',
    forex: 'Divisas',
    
    // Settings
    profile: 'Perfil',
    account: 'Cuenta',
    preferences: 'Preferencias',
    notifications: 'Notificaciones',
    security: 'Seguridad',
    appearance: 'Apariencia',
    language: 'Idioma',
    region: 'Región',
    currency: 'Moneda',
    darkMode: 'Modo Oscuro',
    lightMode: 'Modo Claro',
    
    // Time
    today: 'Hoy',
    yesterday: 'Ayer',
    thisWeek: 'Esta Semana',
    thisMonth: 'Este Mes',
    thisYear: 'Este Año',
    
    // API Status
    connected: 'Conectado',
    disconnected: 'Desconectado',
    apiStatus: 'Estado de API',
    live: 'EN VIVO',
    
    // Analysis Page
    marketOverview: 'Resumen del Mercado',
    recentAnalyses: 'Análisis Recientes',
    confidence: 'Confianza',
    analysisTools: 'Herramientas de Análisis',
    technicalAnalysis: 'Análisis Técnico',
    fundamentalAnalysis: 'Análisis Fundamental',
    priceTargets: 'Objetivos de Precio',
    riskAssessment: 'Evaluación de Riesgo',
    sentimentAnalysis: 'Análisis de Sentimiento',
    valuationModels: 'Modelos de Valoración',
    low: 'Bajo',
    aboveAvg: 'Sobre Promedio',
    breadth: 'Amplitud',
    
    // Portfolio Page
    trackPortfolio: 'Rastrea tus inversiones y rendimiento',
    totalValue: 'Valor Total',
    totalGain: 'Ganancia Total',
    return: 'Retorno',
    totalHoldings: 'Total de Posiciones',
    assetAllocation: 'Distribución de Activos',
    performance: 'Rendimiento',
    stocksBySector: 'Acciones por Sector',
    dividends: 'Dividendos',
    
    // Portfolio additional
    stocks: 'Acciones',
    holdings: 'Posiciones',
    sector: 'Sector',
    shares: 'Acciones',
    avgCost: 'Costo Prom.',
    value: 'Valor',
    gainLoss: 'Gan./Pérd.',
    asset: 'Activo',
    amount: 'Cantidad',
    allocation: 'Distribución',
    assets: 'Activos',
    other: 'Otros',
    estAnnual: 'Est. Anual',
    avgYield: 'Rend. Prom.',
    dividendStocks: 'Acciones con Dividendos',
    dividendPayingHoldings: 'Posiciones que Pagan Dividendos',
    dividendPerShare: 'Dividendo/Acción',
    yield: 'Rendimiento',
    annualIncome: 'Ingreso Anual',
    quarterly: 'Trimestral',
    recentDividendPayments: 'Pagos de Dividendos Recientes',
    currentPrice: 'Precio Actual',
    
    // Settings Page
    generalSettings: 'Configuración General',
    themeLanguageDisplay: 'Preferencias de tema, idioma y visualización',
    switchTheme: 'Cambiar entre tema oscuro y claro',
    timezone: 'Zona Horaria',
    manageNotifications: 'Gestiona cómo recibes alertas y actualizaciones',
    emailNotifications: 'Notificaciones por Email',
    receiveEmail: 'Recibir actualizaciones por email',
    pushNotifications: 'Notificaciones Push',
    browserMobile: 'Alertas del navegador y móvil',
    priceAlerts: 'Alertas de Precio',
    notifiedPrice: 'Recibe notificaciones de movimientos de precio',
    manageAccount: 'Gestiona tu cuenta y preferencias',
    viewProfile: 'Ver Perfil',
    logOut: 'Cerrar Sesión',
  },
  French: {
    // Navigation
    dashboard: 'Tableau de Bord',
    markets: 'Marchés',
    crypto: 'Crypto',
    news: 'Actualités',
    analysis: 'Analyse IA',
    portfolio: 'Portefeuille',
    settings: 'Paramètres',
    
    // Common actions
    search: 'Rechercher des actions...',
    searchNews: 'Rechercher des actualités...',
    viewAll: 'Voir Tout',
    seeAll: 'Voir Tout',
    analyze: 'Analyser',
    refresh: 'Actualiser',
    save: 'Enregistrer',
    cancel: 'Annuler',
    edit: 'Modifier',
    delete: 'Supprimer',
    confirm: 'Confirmer',
    back: 'Retour',
    next: 'Suivant',
    loading: 'Chargement...',
    
    // Dashboard
    marketMovers: 'Mouvements du Marché',
    cryptoMovers: 'Mouvements Crypto',
    watchlist: 'Liste de Suivi',
    quickActions: 'Actions Rapides',
    aiInsights: 'Perspectives IA',
    goodMorning: 'Bonjour',
    goodAfternoon: 'Bon après-midi',
    goodEvening: 'Bonsoir',
    portfolioValue: 'Valeur du Portefeuille',
    totalGain: 'Gain Total',
    todayGain: "Gain d'Aujourd'hui",
    allAccounts: 'Tous les comptes',
    fearGreed: 'Indice de Peur et Cupidité',
    economicIndicators: 'Indicateurs Économiques',
    
    // Market data
    price: 'Prix',
    change: 'Variation',
    volume: 'Volume',
    marketCap: 'Cap. Boursière',
    high: 'Haut',
    low: 'Bas',
    open: 'Ouverture',
    close: 'Clôture',
    
    // Sentiment
    bullish: 'Haussier',
    bearish: 'Baissier',
    neutral: 'Neutre',
    
    // News
    breaking: 'URGENT',
    allNews: 'Toutes les Actualités',
    latestNews: 'Dernières Actualités',
    trending: 'Tendances',
    economy: 'Économie',
    earnings: 'Résultats',
    policy: 'Politique',
    readMore: 'Lire Plus',
    source: 'Source',
    relatedTickers: 'Symboles Associés',
    
    // Portfolio
    holdings: 'Positions',
    totalValue: 'Valeur Totale',
    dayChange: 'Variation du Jour',
    totalReturn: 'Rendement Total',
    shares: 'Actions',
    avgCost: 'Coût Moyen',
    currentPrice: 'Prix Actuel',
    gainLoss: 'Gain/Perte',
    
    // Markets page
    allMarkets: 'Tous les Marchés',
    stocks: 'Actions',
    indices: 'Indices',
    commodities: 'Matières Premières',
    forex: 'Devises',
    
    // Settings
    profile: 'Profil',
    account: 'Compte',
    preferences: 'Préférences',
    notifications: 'Notifications',
    security: 'Sécurité',
    appearance: 'Apparence',
    language: 'Langue',
    region: 'Région',
    currency: 'Devise',
    darkMode: 'Mode Sombre',
    lightMode: 'Mode Clair',
    
    // Time
    today: "Aujourd'hui",
    yesterday: 'Hier',
    thisWeek: 'Cette Semaine',
    thisMonth: 'Ce Mois',
    thisYear: 'Cette Année',
    
    // API Status
    connected: 'Connecté',
    disconnected: 'Déconnecté',
    apiStatus: 'État de l\'API',
    live: 'EN DIRECT',
    
    // Analysis Page
    marketOverview: 'Aperçu du Marché',
    recentAnalyses: 'Analyses Récentes',
    confidence: 'Confiance',
    analysisTools: 'Outils d\'Analyse',
    technicalAnalysis: 'Analyse Technique',
    fundamentalAnalysis: 'Analyse Fondamentale',
    priceTargets: 'Objectifs de Prix',
    riskAssessment: 'Évaluation des Risques',
    sentimentAnalysis: 'Analyse de Sentiment',
    valuationModels: 'Modèles de Valorisation',
    low: 'Bas',
    aboveAvg: 'Au-dessus Moy.',
    breadth: 'Étendue',
    
    // Portfolio Page
    trackPortfolio: 'Suivez vos investissements et performances',
    totalValue: 'Valeur Totale',
    totalGain: 'Gain Total',
    return: 'Rendement',
    totalHoldings: 'Total des Positions',
    assetAllocation: 'Répartition des Actifs',
    performance: 'Performance',
    stocksBySector: 'Actions par Secteur',
    dividends: 'Dividendes',
    
    // Portfolio additional
    stocks: 'Actions',
    holdings: 'Positions',
    sector: 'Secteur',
    shares: 'Actions',
    avgCost: 'Coût Moy.',
    value: 'Valeur',
    gainLoss: 'Gain/Perte',
    asset: 'Actif',
    amount: 'Montant',
    allocation: 'Répartition',
    assets: 'Actifs',
    other: 'Autres',
    estAnnual: 'Est. Annuel',
    avgYield: 'Rend. Moy.',
    dividendStocks: 'Actions à Dividendes',
    dividendPayingHoldings: 'Positions Versant des Dividendes',
    dividendPerShare: 'Dividende/Action',
    yield: 'Rendement',
    annualIncome: 'Revenu Annuel',
    quarterly: 'Trimestriel',
    recentDividendPayments: 'Paiements de Dividendes Récents',
    currentPrice: 'Prix Actuel',
    
    // Settings Page
    generalSettings: 'Paramètres Généraux',
    themeLanguageDisplay: 'Préférences de thème, langue et affichage',
    switchTheme: 'Basculer entre thème sombre et clair',
    timezone: 'Fuseau Horaire',
    manageNotifications: 'Gérez comment vous recevez alertes et mises à jour',
    emailNotifications: 'Notifications par Email',
    receiveEmail: 'Recevoir les mises à jour par email',
    pushNotifications: 'Notifications Push',
    browserMobile: 'Alertes navigateur et mobile',
    priceAlerts: 'Alertes de Prix',
    notifiedPrice: 'Être notifié des mouvements de prix',
    manageAccount: 'Gérez votre compte et préférences',
    viewProfile: 'Voir le Profil',
    logOut: 'Déconnexion',
  },
  German: {
    // Navigation
    dashboard: 'Dashboard',
    markets: 'Märkte',
    crypto: 'Krypto',
    news: 'Nachrichten',
    analysis: 'KI-Analyse',
    portfolio: 'Portfolio',
    settings: 'Einstellungen',
    
    // Common actions
    search: 'Aktien suchen...',
    searchNews: 'Nachrichten suchen...',
    viewAll: 'Alle Anzeigen',
    seeAll: 'Alle Anzeigen',
    analyze: 'Analysieren',
    refresh: 'Aktualisieren',
    save: 'Speichern',
    cancel: 'Abbrechen',
    edit: 'Bearbeiten',
    delete: 'Löschen',
    confirm: 'Bestätigen',
    back: 'Zurück',
    next: 'Weiter',
    loading: 'Laden...',
    
    // Dashboard
    marketMovers: 'Marktbeweger',
    cryptoMovers: 'Krypto-Beweger',
    watchlist: 'Beobachtungsliste',
    quickActions: 'Schnellaktionen',
    aiInsights: 'KI-Einblicke',
    goodMorning: 'Guten Morgen',
    goodAfternoon: 'Guten Tag',
    goodEvening: 'Guten Abend',
    portfolioValue: 'Portfolio-Wert',
    totalGain: 'Gesamtgewinn',
    todayGain: 'Heutiger Gewinn',
    allAccounts: 'Alle Konten',
    fearGreed: 'Angst & Gier Index',
    economicIndicators: 'Wirtschaftsindikatoren',
    
    // Market data
    price: 'Preis',
    change: 'Änderung',
    volume: 'Volumen',
    marketCap: 'Marktkapitalisierung',
    high: 'Hoch',
    low: 'Tief',
    open: 'Eröffnung',
    close: 'Schluss',
    
    // Sentiment
    bullish: 'Bullisch',
    bearish: 'Bärisch',
    neutral: 'Neutral',
    
    // News
    breaking: 'EILMELDUNG',
    allNews: 'Alle Nachrichten',
    latestNews: 'Neueste Nachrichten',
    trending: 'Trends',
    economy: 'Wirtschaft',
    earnings: 'Ergebnisse',
    policy: 'Politik',
    readMore: 'Weiterlesen',
    source: 'Quelle',
    relatedTickers: 'Verwandte Symbole',
    
    // Portfolio
    holdings: 'Bestände',
    totalValue: 'Gesamtwert',
    dayChange: 'Tagesänderung',
    totalReturn: 'Gesamtrendite',
    shares: 'Aktien',
    avgCost: 'Durchschnittskosten',
    currentPrice: 'Aktueller Preis',
    gainLoss: 'Gewinn/Verlust',
    
    // Markets page
    allMarkets: 'Alle Märkte',
    stocks: 'Aktien',
    indices: 'Indizes',
    commodities: 'Rohstoffe',
    forex: 'Devisen',
    
    // Settings
    profile: 'Profil',
    account: 'Konto',
    preferences: 'Präferenzen',
    notifications: 'Benachrichtigungen',
    security: 'Sicherheit',
    appearance: 'Erscheinung',
    language: 'Sprache',
    region: 'Region',
    currency: 'Währung',
    darkMode: 'Dunkelmodus',
    lightMode: 'Hellmodus',
    
    // Time
    today: 'Heute',
    yesterday: 'Gestern',
    thisWeek: 'Diese Woche',
    thisMonth: 'Dieser Monat',
    thisYear: 'Dieses Jahr',
    
    // API Status
    connected: 'Verbunden',
    disconnected: 'Getrennt',
    apiStatus: 'API-Status',
    live: 'LIVE',
    
    // Analysis Page
    marketOverview: 'Marktübersicht',
    recentAnalyses: 'Aktuelle Analysen',
    confidence: 'Zuversicht',
    analysisTools: 'Analyse-Tools',
    technicalAnalysis: 'Technische Analyse',
    fundamentalAnalysis: 'Fundamentalanalyse',
    priceTargets: 'Kursziele',
    riskAssessment: 'Risikobewertung',
    sentimentAnalysis: 'Stimmungsanalyse',
    valuationModels: 'Bewertungsmodelle',
    low: 'Niedrig',
    aboveAvg: 'Über Durchschn.',
    breadth: 'Breite',
    
    // Portfolio Page
    trackPortfolio: 'Verfolgen Sie Ihre Investitionen und Performance',
    totalValue: 'Gesamtwert',
    totalGain: 'Gesamtgewinn',
    return: 'Rendite',
    totalHoldings: 'Gesamtbestände',
    assetAllocation: 'Vermögensaufteilung',
    performance: 'Performance',
    stocksBySector: 'Aktien nach Sektor',
    dividends: 'Dividenden',
    
    // Portfolio additional
    stocks: 'Aktien',
    holdings: 'Bestände',
    sector: 'Sektor',
    shares: 'Anteile',
    avgCost: 'Durchschn. Kosten',
    value: 'Wert',
    gainLoss: 'Gewinn/Verlust',
    asset: 'Anlage',
    amount: 'Menge',
    allocation: 'Aufteilung',
    assets: 'Anlagen',
    other: 'Sonstige',
    estAnnual: 'Gesch. Jährlich',
    avgYield: 'Durchschn. Rendite',
    dividendStocks: 'Dividendenaktien',
    dividendPayingHoldings: 'Dividendenzahlende Positionen',
    dividendPerShare: 'Dividende/Aktie',
    yield: 'Rendite',
    annualIncome: 'Jahreseinkommen',
    quarterly: 'Vierteljährlich',
    recentDividendPayments: 'Aktuelle Dividendenzahlungen',
    currentPrice: 'Aktueller Preis',
    
    // Settings Page
    generalSettings: 'Allgemeine Einstellungen',
    themeLanguageDisplay: 'Theme-, Sprach- und Anzeigeeinstellungen',
    switchTheme: 'Zwischen dunklem und hellem Theme wechseln',
    timezone: 'Zeitzone',
    manageNotifications: 'Verwalten Sie Benachrichtigungen und Updates',
    emailNotifications: 'E-Mail-Benachrichtigungen',
    receiveEmail: 'Updates per E-Mail erhalten',
    pushNotifications: 'Push-Benachrichtigungen',
    browserMobile: 'Browser- und Mobile-Alarme',
    priceAlerts: 'Preisalarme',
    notifiedPrice: 'Bei Preisbewegungen benachrichtigt werden',
    manageAccount: 'Konto und Einstellungen verwalten',
    viewProfile: 'Profil anzeigen',
    logOut: 'Abmelden',
  },
  Chinese: {
    // Navigation
    dashboard: '仪表板',
    markets: '市场',
    crypto: '加密货币',
    news: '新闻',
    analysis: 'AI分析',
    portfolio: '投资组合',
    settings: '设置',
    
    // Common actions
    search: '搜索股票...',
    searchNews: '搜索新闻...',
    viewAll: '查看全部',
    seeAll: '查看全部',
    analyze: '分析',
    refresh: '刷新',
    save: '保存',
    cancel: '取消',
    edit: '编辑',
    delete: '删除',
    confirm: '确认',
    back: '返回',
    next: '下一步',
    loading: '加载中...',
    
    // Dashboard
    marketMovers: '市场动态',
    cryptoMovers: '加密动态',
    watchlist: '自选股',
    quickActions: '快捷操作',
    aiInsights: 'AI洞察',
    goodMorning: '早上好',
    goodAfternoon: '下午好',
    goodEvening: '晚上好',
    portfolioValue: '投资组合价值',
    totalGain: '总收益',
    todayGain: '今日收益',
    allAccounts: '所有账户',
    fearGreed: '恐惧与贪婪指数',
    economicIndicators: '经济指标',
    
    // Market data
    price: '价格',
    change: '涨跌',
    volume: '成交量',
    marketCap: '市值',
    high: '最高',
    low: '最低',
    open: '开盘',
    close: '收盘',
    
    // Sentiment
    bullish: '看涨',
    bearish: '看跌',
    neutral: '中性',
    
    // News
    breaking: '突发',
    allNews: '所有新闻',
    latestNews: '最新新闻',
    trending: '热门',
    economy: '经济',
    earnings: '财报',
    policy: '政策',
    readMore: '阅读更多',
    source: '来源',
    relatedTickers: '相关股票',
    
    // Portfolio
    holdings: '持仓',
    totalValue: '总价值',
    dayChange: '日涨跌',
    totalReturn: '总回报',
    shares: '股数',
    avgCost: '平均成本',
    currentPrice: '当前价格',
    gainLoss: '盈亏',
    
    // Markets page
    allMarkets: '所有市场',
    stocks: '股票',
    indices: '指数',
    commodities: '商品',
    forex: '外汇',
    
    // Settings
    profile: '个人资料',
    account: '账户',
    preferences: '偏好设置',
    notifications: '通知',
    security: '安全',
    appearance: '外观',
    language: '语言',
    region: '地区',
    currency: '货币',
    darkMode: '深色模式',
    lightMode: '浅色模式',
    
    // Time
    today: '今天',
    yesterday: '昨天',
    thisWeek: '本周',
    thisMonth: '本月',
    thisYear: '今年',
    
    // API Status
    connected: '已连接',
    disconnected: '已断开',
    apiStatus: 'API状态',
    live: '实时',
    
    // Analysis Page
    marketOverview: '市场概览',
    recentAnalyses: '最近分析',
    confidence: '置信度',
    analysisTools: '分析工具',
    technicalAnalysis: '技术分析',
    fundamentalAnalysis: '基本面分析',
    priceTargets: '目标价格',
    riskAssessment: '风险评估',
    sentimentAnalysis: '情绪分析',
    valuationModels: '估值模型',
    low: '低',
    aboveAvg: '高于平均',
    breadth: '广度',
    
    // Portfolio Page
    trackPortfolio: '跟踪您的投资和业绩',
    totalValue: '总价值',
    totalGain: '总收益',
    return: '回报',
    totalHoldings: '总持仓',
    assetAllocation: '资产配置',
    performance: '业绩',
    stocksBySector: '按行业分类',
    dividends: '股息',
    
    // Portfolio additional
    stocks: '股票',
    holdings: '持仓',
    sector: '行业',
    shares: '股数',
    avgCost: '平均成本',
    value: '价值',
    gainLoss: '盈亏',
    asset: '资产',
    amount: '数量',
    allocation: '配置',
    assets: '资产',
    other: '其他',
    estAnnual: '预估年度',
    avgYield: '平均收益率',
    dividendStocks: '分红股票',
    dividendPayingHoldings: '支付股息的持仓',
    dividendPerShare: '每股股息',
    yield: '收益率',
    annualIncome: '年收入',
    quarterly: '季度',
    recentDividendPayments: '最近股息支付',
    currentPrice: '当前价格',
    
    // Settings Page
    generalSettings: '通用设置',
    themeLanguageDisplay: '主题、语言和显示偏好',
    switchTheme: '切换深色和浅色主题',
    timezone: '时区',
    manageNotifications: '管理您接收提醒和更新的方式',
    emailNotifications: '邮件通知',
    receiveEmail: '通过邮件接收更新',
    pushNotifications: '推送通知',
    browserMobile: '浏览器和移动提醒',
    priceAlerts: '价格提醒',
    notifiedPrice: '获取价格变动通知',
    manageAccount: '管理您的账户和偏好',
    viewProfile: '查看资料',
    logOut: '退出登录',
  },
  Japanese: {
    // Navigation
    dashboard: 'ダッシュボード',
    markets: 'マーケット',
    crypto: '暗号通貨',
    news: 'ニュース',
    analysis: 'AI分析',
    portfolio: 'ポートフォリオ',
    settings: '設定',
    
    // Common actions
    search: '銘柄を検索...',
    searchNews: 'ニュースを検索...',
    viewAll: 'すべて見る',
    seeAll: 'すべて見る',
    analyze: '分析',
    refresh: '更新',
    save: '保存',
    cancel: 'キャンセル',
    edit: '編集',
    delete: '削除',
    confirm: '確認',
    back: '戻る',
    next: '次へ',
    loading: '読み込み中...',
    
    // Dashboard
    marketMovers: '市場の動き',
    cryptoMovers: '暗号通貨の動き',
    watchlist: 'ウォッチリスト',
    quickActions: 'クイックアクション',
    aiInsights: 'AIインサイト',
    goodMorning: 'おはようございます',
    goodAfternoon: 'こんにちは',
    goodEvening: 'こんばんは',
    portfolioValue: 'ポートフォリオ価値',
    totalGain: '総利益',
    todayGain: '本日の利益',
    allAccounts: 'すべてのアカウント',
    fearGreed: '恐怖と強欲指数',
    economicIndicators: '経済指標',
    
    // Market data
    price: '価格',
    change: '変動',
    volume: '出来高',
    marketCap: '時価総額',
    high: '高値',
    low: '安値',
    open: '始値',
    close: '終値',
    
    // Sentiment
    bullish: '強気',
    bearish: '弱気',
    neutral: '中立',
    
    // News
    breaking: '速報',
    allNews: 'すべてのニュース',
    latestNews: '最新ニュース',
    trending: 'トレンド',
    economy: '経済',
    earnings: '決算',
    policy: '政策',
    readMore: '続きを読む',
    source: 'ソース',
    relatedTickers: '関連銘柄',
    
    // Portfolio
    holdings: '保有',
    totalValue: '総価値',
    dayChange: '日次変動',
    totalReturn: '総リターン',
    shares: '株数',
    avgCost: '平均取得単価',
    currentPrice: '現在価格',
    gainLoss: '損益',
    
    // Markets page
    allMarkets: 'すべての市場',
    stocks: '株式',
    indices: '指数',
    commodities: 'コモディティ',
    forex: '為替',
    
    // Settings
    profile: 'プロフィール',
    account: 'アカウント',
    preferences: '設定',
    notifications: '通知',
    security: 'セキュリティ',
    appearance: '外観',
    language: '言語',
    region: '地域',
    currency: '通貨',
    darkMode: 'ダークモード',
    lightMode: 'ライトモード',
    
    // Time
    today: '今日',
    yesterday: '昨日',
    thisWeek: '今週',
    thisMonth: '今月',
    thisYear: '今年',
    
    // API Status
    connected: '接続済み',
    disconnected: '切断',
    apiStatus: 'API状態',
    live: 'ライブ',
    
    // Analysis Page
    marketOverview: '市場概要',
    recentAnalyses: '最近の分析',
    confidence: '信頼度',
    analysisTools: '分析ツール',
    technicalAnalysis: 'テクニカル分析',
    fundamentalAnalysis: 'ファンダメンタル分析',
    priceTargets: '目標株価',
    riskAssessment: 'リスク評価',
    sentimentAnalysis: 'センチメント分析',
    valuationModels: 'バリュエーションモデル',
    low: '低い',
    aboveAvg: '平均以上',
    breadth: '幅',
    
    // Portfolio Page
    trackPortfolio: '投資とパフォーマンスを追跡',
    totalValue: '総価値',
    totalGain: '総利益',
    return: 'リターン',
    totalHoldings: '総保有数',
    assetAllocation: '資産配分',
    performance: 'パフォーマンス',
    stocksBySector: 'セクター別株式',
    dividends: '配当',
    
    // Portfolio additional
    stocks: '株式',
    holdings: '保有',
    sector: 'セクター',
    shares: '株数',
    avgCost: '平均コスト',
    value: '価値',
    gainLoss: '損益',
    asset: '資産',
    amount: '数量',
    allocation: '配分',
    assets: '資産',
    other: 'その他',
    estAnnual: '推定年間',
    avgYield: '平均利回り',
    dividendStocks: '配当株',
    dividendPayingHoldings: '配当支払い保有',
    dividendPerShare: '1株配当',
    yield: '利回り',
    annualIncome: '年間収入',
    quarterly: '四半期',
    recentDividendPayments: '最近の配当支払い',
    currentPrice: '現在の価格',
    
    // Settings Page
    generalSettings: '一般設定',
    themeLanguageDisplay: 'テーマ、言語、表示設定',
    switchTheme: 'ダークとライトテーマの切り替え',
    timezone: 'タイムゾーン',
    manageNotifications: 'アラートと更新の受け取り方を管理',
    emailNotifications: 'メール通知',
    receiveEmail: 'メールで更新を受け取る',
    pushNotifications: 'プッシュ通知',
    browserMobile: 'ブラウザとモバイルアラート',
    priceAlerts: '価格アラート',
    notifiedPrice: '価格変動の通知を受け取る',
    manageAccount: 'アカウントと設定を管理',
    viewProfile: 'プロフィールを見る',
    logOut: 'ログアウト',
  },
};

// Provider component
export function UserPreferencesProvider({ children }) {
  // Load from localStorage or use defaults
  const [preferences, setPreferences] = useState(() => {
    const saved = localStorage.getItem('userPreferences');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch {
        return getDefaultPreferences();
      }
    }
    return getDefaultPreferences();
  });

  const [notifications, setNotifications] = useState([
    { id: 1, type: 'alert', message: 'NVDA up 5% - Price target reached', time: '2m ago', read: false },
    { id: 2, type: 'news', message: 'Fed announces rate decision', time: '15m ago', read: false },
    { id: 3, type: 'portfolio', message: 'Your portfolio is up 2.3% today', time: '1h ago', read: false },
  ]);

  function getDefaultPreferences() {
    return {
      name: 'User',
      email: 'user@example.com',
      region: 'US',
      currency: 'USD',
      language: 'English',
      darkMode: true,
      notificationsEnabled: true,
    };
  }

  // Save to localStorage when preferences change
  useEffect(() => {
    localStorage.setItem('userPreferences', JSON.stringify(preferences));
  }, [preferences]);

  // Update a single preference
  const updatePreference = (key, value) => {
    setPreferences(prev => ({ ...prev, [key]: value }));
  };

  // Update multiple preferences
  const updatePreferences = (updates) => {
    setPreferences(prev => ({ ...prev, ...updates }));
  };

  // Convert price from USD to selected currency
  const convertPrice = (priceInUSD) => {
    const rate = currencyRates[preferences.currency] || 1;
    return priceInUSD * rate;
  };

  // Format price with currency symbol
  const formatPrice = (priceInUSD, options = {}) => {
    const { showSymbol = true, decimals = 2 } = options;
    const converted = convertPrice(priceInUSD);
    const symbol = currencySymbols[preferences.currency] || '$';
    
    // Handle crypto currencies differently
    if (preferences.currency === 'BTC' || preferences.currency === 'ETH') {
      const formatted = converted.toFixed(6);
      return showSymbol ? `${symbol}${formatted}` : formatted;
    }
    
    // Handle JPY/KRW (no decimals typically)
    if (preferences.currency === 'JPY' || preferences.currency === 'KRW') {
      const formatted = Math.round(converted).toLocaleString();
      return showSymbol ? `${symbol}${formatted}` : formatted;
    }
    
    const formatted = converted.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    
    return showSymbol ? `${symbol}${formatted}` : formatted;
  };

  // Get translation
  const t = (key) => {
    const langTranslations = translations[preferences.language] || translations.English;
    return langTranslations[key] || translations.English[key] || key;
  };

  // Get exchanges for current region
  const getExchanges = () => {
    return regionExchanges[preferences.region] || regionExchanges.GLOBAL;
  };

  // Check if exchange is in current region
  const isExchangeInRegion = (exchange) => {
    const exchanges = getExchanges();
    return exchanges.includes(exchange);
  };

  // Mark notification as read
  const markNotificationRead = (id) => {
    setNotifications(prev => 
      prev.map(n => n.id === id ? { ...n, read: true } : n)
    );
  };

  // Clear all notifications
  const clearNotifications = () => {
    setNotifications([]);
  };

  // Get unread notification count
  const unreadCount = notifications.filter(n => !n.read).length;

  const value = {
    preferences,
    updatePreference,
    updatePreferences,
    convertPrice,
    formatPrice,
    t,
    getExchanges,
    isExchangeInRegion,
    currencySymbols,
    currencyRates,
    notifications,
    unreadCount,
    markNotificationRead,
    clearNotifications,
  };

  return (
    <UserPreferencesContext.Provider value={value}>
      {children}
    </UserPreferencesContext.Provider>
  );
}

// Hook to use preferences
export function useUserPreferences() {
  const context = useContext(UserPreferencesContext);
  if (!context) {
    throw new Error('useUserPreferences must be used within a UserPreferencesProvider');
  }
  return context;
}

export default UserPreferencesContext;